/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react';
import analytics, { events, trackUpgradeStarted } from '../../../../utils/analytics';
import { UPGRADE_PAGE_URL } from '../../../../utils/constants';
import env from '../../../../utils/env';
import { compose } from '../../../../utils/fp';
import BaseModal from '../BaseModal/BaseModal';
import {
  addCurrency,
  formatPrice,
  formatQty,
  prependText,
  wrapWithParens,
} from '../utils/helperFuncs';
import Grouping from './grouping';
import ProductPrice from './ProductPrice/ProductPrice';
import { ProductDetailType, ProductProps } from './types';
import fallback from './fallback.svg';
import * as classNames from './Product.module.css';

const UPGRADE_CTA = 'Upgrade to Unlock';

const priceWithCurrency = compose(addCurrency, formatPrice);
const formatAddonPrice = compose(wrapWithParens, prependText('+'), priceWithCurrency);
const getEditUrl = (isBoost: boolean, customizeQuestionId: number | null): string =>
  isBoost ? `${env.envURL}/boost` : `${env.envURL}/customize/?q=${customizeQuestionId}`;

const Product = (props: ProductProps) => {
  const {
    exploreDisplay,
    customization,
    customizeQuestionId,
    grouping,
    isSelect,
    brand,
    name,
    extraChoicePrice,
    productPageUrl,
    quantity,
    primaryQuantity = 0,
    additionalQuantity = 0,
    retailPrice,
    open,
    url,
    hasBeenAssigned,
    productLink,
    isBoost,
    winter2020MysteryChoice,
    description,
    pdpId,
    cadenceType,
    nextRenewalDate,
    selectProductDetail,
    saleUuid,
    slug,
    productId,
    subscriptionLimit,
    isActive,
    isCancelled,
    price,
    salePrice,
  } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const isSubscription = !!cadenceType;
  const productName = brand ? `${brand} ${name}` : name;
  const subscriptionProduct = {
    name: productName,
    imageUrl: url || '',
    price: price || retailPrice,
  };
  const handleViewDetails = () => {
    const productDetail = {
      productId,
      brand,
      name,
      quantity,
      cadenceType,
      nextRenewalDate,
      url,
      description,
      subscriptionLimit,
      isActive,
      isCancelled,
    } satisfies ProductDetailType;
    selectProductDetail(productDetail);
  };

  const itemQty = formatQty(
    quantity === undefined ? primaryQuantity + additionalQuantity : quantity,
  );

  const formatSalePriceWithQty = compose(prependText(itemQty), priceWithCurrency);

  //Display Containers
  const questionIdDisplay = () => (
    <div className={classNames.question}>{prependText('Customization ')(customizeQuestionId)}</div>
  );

  const canEditQuestion = () =>
    open &&
    (isBoost ||
      (customizeQuestionId &&
        (grouping !== Grouping.SELECT || (grouping === Grouping.SELECT && isSelect))));

  const canViewItem = () => {
    if (isBoost) {
      return !open;
    }
    if (exploreDisplay) {
      return productPageUrl || description;
    }
    return productPageUrl || description;
  };

  const getProductImg = () => (
    <div className={classNames.imageContainer}>
      <img className={classNames.thumbnail} src={url || fallback} alt={productName} />
    </div>
  );

  const getProductLink = () => {
    if (slug) return `${env.originURL}/product/${slug}${saleUuid ? `?saleUuid=${saleUuid}` : ''}`;
    if (open && !hasBeenAssigned) return productPageUrl;
    if (!pdpId) return undefined;

    return `${env.originURL}/product/${pdpId}`;
  };

  const getAnalyticsViewItemTitle = () =>
    isBoost ? `Boost - ${productName}` : `Add-Ons - ${productName}`;

  const getAnalyticsEditItemTitle = () =>
    isBoost ? `Boost - ${productName}` : `Customize - ${productName}`;

  return (
    <div className={classNames.container}>
      <BaseModal
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        shouldCloseOnOverlayClick
        styles={{ maxWidth: '100%', width: 400 }}
      >
        <div className={classNames.modalContent}>
          <button
            type="button"
            onClick={() => setModalOpen(false)}
            className={classNames.closeButton}
          >
            <i className={classNames.closeIcon} />
          </button>
          {}
          <div dangerouslySetInnerHTML={{ __html: description as string }} />
        </div>
      </BaseModal>
      <div>
        {isSubscription ? (
          <div className={classNames.imageContainer}>
            <img
              className={classNames.thumbnail}
              src={subscriptionProduct.imageUrl || fallback}
              alt={subscriptionProduct.name}
            />
          </div>
        ) : (
          <a
            onClick={() => {
              analytics.track(events.CLICKED_ELEMENT, {
                element_name: 'View Item',
                option_title: getAnalyticsViewItemTitle(),
              });
            }}
            href={getProductLink()}
            className={classNames.imageAnchor}
          >
            {getProductImg()}
          </a>
        )}
      </div>
      <div className={classNames.infoContainer}>
        <div className={classNames.info}>
          {customization && !exploreDisplay && questionIdDisplay()}

          {/* Additional Choice price display */}
          {extraChoicePrice && !exploreDisplay && (
            <div className={classNames.price}>{formatAddonPrice(extraChoicePrice)}</div>
          )}

          {/* Product Name */}
          <div className={classNames.name}>{subscriptionProduct.name}</div>

          {/* price display */}
          {isSubscription ? (
            <ProductPrice
              name={subscriptionProduct.name || ''}
              salePrice={price}
              formatSalePriceWithQty={formatSalePriceWithQty}
              priceWithCurrency={priceWithCurrency}
            />
          ) : (
            <ProductPrice
              name={name}
              winter2020MysteryChoice={winter2020MysteryChoice}
              salePrice={salePrice}
              retailPrice={retailPrice}
              isBoost={isBoost}
              formatSalePriceWithQty={formatSalePriceWithQty}
              priceWithCurrency={priceWithCurrency}
            />
          )}

          {/* Select Label Unlocked */}
          {grouping === Grouping.SELECT && (
            <div className={classNames.selectText}>
              <i className={isSelect ? classNames.unlockedIcon : classNames.lockedIcon} />
              <span>Annual Only</span>
            </div>
          )}

          {/* Edit Item @TODO Connect to product page */}
          {canViewItem() && !isSubscription && !!productLink && (
            <div className={classNames.linkContainer}>
              <a
                onClick={() => {
                  analytics.track(events.CLICKED_ELEMENT, {
                    element_name: 'View Item',
                    option_title: getAnalyticsViewItemTitle(),
                  });
                }}
                href={getProductLink()}
              >
                View Item
              </a>
            </div>
          )}

          {/* TODO: Review this - Should we show the view item link for subscriptions? */}
          {canViewItem() && !isSubscription && !!description && !productLink && (
            <div className={classNames.linkContainer}>
              {/* TODO: Please fix the a11y issues with this element and then remove the comments at the top of the file. */}
              <a
                onClick={() => {
                  analytics.track(events.CLICKED_ELEMENT, {
                    element_name: 'View Item',
                    option_title: getAnalyticsViewItemTitle(),
                  });
                  setModalOpen(true);
                }}
                className={classNames.modalLink}
              >
                View Item
              </a>
            </div>
          )}

          {canEditQuestion() && (
            <div className={classNames.linkContainer}>
              <a
                onClick={() => {
                  analytics.track(events.CLICKED_ELEMENT, {
                    element_name: 'Edit',
                    option_title: getAnalyticsEditItemTitle(),
                  });
                }}
                href={getEditUrl(isBoost, customizeQuestionId)}
              >
                Edit
              </a>
            </div>
          )}

          {/* View Details only for subscription products */}
          {isSubscription && (
            <div className={classNames.linkContainer}>
              <a onClick={handleViewDetails} className={classNames.modalLink}>
                View Details
              </a>
            </div>
          )}

          {/* Upgrade to Unlock */}
          {customization && !exploreDisplay && !canEditQuestion() && open && (
            <div className={classNames.linkContainer}>
              <a
                onClick={() => {
                  trackUpgradeStarted(UPGRADE_CTA);
                }}
                href={UPGRADE_PAGE_URL}
              >
                {UPGRADE_CTA}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Product;
